import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { contactIntro, contactHeading, contactTopics, stageOptions } from "@/content/site";
import { landPositionOptions } from "@/content/funding";
import { supportTypeOptions } from "@/content/projectFunding";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  topic: "",
  organisation: "",
  location: "",
  phone: "",
  land_access_status: "",
  support_type: "",
  funding_deadline: "",
  message: "",
  stage: "",
  privacy_consent: false,
  website: "", // honeypot — hidden from real users
};

const inputClasses =
  "rounded-sm border-charcoal/30 bg-cream focus-visible:ring-ochre text-charcoal placeholder:text-charcoal/40";

export default function Contact() {
  const [searchParams] = useSearchParams();
  // Legacy topic values from earlier page CTAs map onto the new list.
  const legacyTopicMap = {
    "land-access-clt": "project-development",
    "funding-preparation": "project-development",
    "engagement-governance": "project-development",
    "community-enterprise": "project-development",
    consulting: "project-development",
    "funding-grants": "project-development",
    "capital-partners": "partnership",
    "philanthropic-partnership": "partnership",
  };
  const rawTopic = searchParams.get("topic") || "";
  const mappedTopic = legacyTopicMap[rawTopic] || rawTopic;
  const initialTopic = contactTopics.some((t) => t.value === mappedTopic) ? mappedTopic : "";
  const [form, setForm] = useState({ ...initialForm, topic: initialTopic });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const update = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = "Please enter a valid email address.";
    if (form.message.trim().length < 10)
      next.message = "Please tell us a little more (at least 10 characters).";
    if (!form.privacy_consent)
      next.privacy_consent = "Please confirm you have read the privacy note.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        topic: form.topic || null,
        organisation: form.organisation.trim() || null,
        location: form.location.trim() || null,
        phone: form.phone.trim() || null,
        land_access_status: form.land_access_status || null,
        support_type: form.support_type || null,
        funding_deadline: form.funding_deadline.trim() || null,
        message: form.message.trim(),
        stage: form.stage || null,
        privacy_consent: form.privacy_consent,
        website: form.website,
      });
      if (data.email_delivery === "sent") {
        toast.success("Thank you — your enquiry is on its way.");
        setStatusMessage({
          type: "success",
          text: "Thank you for getting in touch. Your enquiry has been received.",
        });
        setForm(initialForm);
      } else {
        // Honest degraded state: stored, but email notification is not configured/working.
        toast.warning("Your enquiry was recorded, with a note below.");
        setStatusMessage({
          type: "warning",
          text: "Your enquiry has been recorded securely. There is currently a problem with email delivery on this site, so there may be a delay before it is seen — please accept our apologies, and feel free to try again shortly.",
        });
        setForm(initialForm);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong sending your enquiry. Please check the details and try again.";
      toast.error("Your enquiry could not be sent.");
      setStatusMessage({ type: "error", text: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact | Bioregional Ventures"
        description="Considering a community land trust, cooperative farm or restoration project? Start a conversation with Bioregional Ventures."
      />

      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <Reveal className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-4">Contact</p>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight text-charcoal" data-testid="contact-heading">
              {contactHeading}
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-charcoal/90">
              {contactIntro}
            </p>
            <p className="mt-6 text-sm leading-relaxed text-charcoal/70">
              We read every enquiry personally. There is no obligation and no cost for an initial
              conversation.
            </p>
          </Reveal>

          <Reveal className="md:col-span-7" delay={0.08}>
            <form
              onSubmit={handleSubmit}
              noValidate
              data-testid="contact-form"
              className="border border-charcoal/15 bg-cream p-7 md:p-10 space-y-6"
            >
              {/* Honeypot: hidden from users, visible to bots */}
              <div className="absolute opacity-0 -z-10 h-0 w-0 overflow-hidden" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => update("website", e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-sm text-charcoal">
                    Name <span className="text-ochre-dark" aria-hidden="true">*</span>
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    className={`mt-2 ${inputClasses}`}
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "name-error" : undefined}
                    autoComplete="name"
                  />
                  {errors.name && (
                    <p id="name-error" role="alert" className="mt-2 text-sm text-destructive" data-testid="contact-name-error">
                      {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm text-charcoal">
                    Email <span className="text-ochre-dark" aria-hidden="true">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    className={`mt-2 ${inputClasses}`}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "email-error" : undefined}
                    autoComplete="email"
                  />
                  {errors.email && (
                    <p id="email-error" role="alert" className="mt-2 text-sm text-destructive" data-testid="contact-email-error">
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="topic" className="text-sm text-charcoal">
                  What would you like to discuss? <span className="text-charcoal/50">(optional)</span>
                </Label>
                <Select value={form.topic} onValueChange={(v) => update("topic", v)}>
                  <SelectTrigger
                    id="topic"
                    data-testid="contact-topic-select"
                    className={`mt-2 ${inputClasses}`}
                    aria-invalid={!!errors.topic}
                    aria-describedby={errors.topic ? "topic-error" : undefined}
                  >
                    <SelectValue placeholder="Select a topic" />
                  </SelectTrigger>
                  <SelectContent className="bg-cream border-charcoal/20 rounded-sm">
                    {contactTopics.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value}
                        data-testid={`contact-topic-option-${opt.value}`}
                        className="focus:bg-sage"
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.topic && (
                  <p id="topic-error" role="alert" className="mt-2 text-sm text-destructive" data-testid="contact-topic-error">
                    {errors.topic}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="organisation" className="text-sm text-charcoal">
                    Organisation <span className="text-charcoal/50">(optional)</span>
                  </Label>
                  <Input
                    id="organisation"
                    data-testid="contact-organisation-input"
                    className={`mt-2 ${inputClasses}`}
                    value={form.organisation}
                    onChange={(e) => update("organisation", e.target.value)}
                    autoComplete="organization"
                  />
                </div>
                <div>
                  <Label htmlFor="location" className="text-sm text-charcoal">
                    Location or region <span className="text-charcoal/50">(optional)</span>
                  </Label>
                  <Input
                    id="location"
                    data-testid="contact-location-input"
                    className={`mt-2 ${inputClasses}`}
                    value={form.location}
                    onChange={(e) => update("location", e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="text-sm text-charcoal">
                    Phone <span className="text-charcoal/50">(optional)</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="contact-phone-input"
                    className={`mt-2 ${inputClasses}`}
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    autoComplete="tel"
                  />
                </div>
                <div>
                  <Label htmlFor="land_access_status" className="text-sm text-charcoal">
                    Land access status <span className="text-charcoal/50">(optional)</span>
                  </Label>
                  <Select value={form.land_access_status} onValueChange={(v) => update("land_access_status", v)}>
                    <SelectTrigger
                      id="land_access_status"
                      data-testid="contact-land-status-select"
                      className={`mt-2 ${inputClasses}`}
                    >
                      <SelectValue placeholder="Select if relevant" />
                    </SelectTrigger>
                    <SelectContent className="bg-cream border-charcoal/20 rounded-sm">
                      {landPositionOptions.map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          data-testid={`contact-land-status-option-${opt.value}`}
                          className="focus:bg-sage"
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="support_type" className="text-sm text-charcoal">
                    Type of support sought <span className="text-charcoal/50">(optional)</span>
                  </Label>
                  <Select value={form.support_type} onValueChange={(v) => update("support_type", v)}>
                    <SelectTrigger
                      id="support_type"
                      data-testid="contact-support-type-select"
                      className={`mt-2 ${inputClasses}`}
                    >
                      <SelectValue placeholder="Select if relevant" />
                    </SelectTrigger>
                    <SelectContent className="bg-cream border-charcoal/20 rounded-sm">
                      {supportTypeOptions.map((opt) => (
                        <SelectItem
                          key={opt}
                          value={opt}
                          data-testid={`contact-support-type-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="focus:bg-sage"
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="funding_deadline" className="text-sm text-charcoal">
                    Funding deadline <span className="text-charcoal/50">(optional)</span>
                  </Label>
                  <Input
                    id="funding_deadline"
                    data-testid="contact-deadline-input"
                    className={`mt-2 ${inputClasses}`}
                    placeholder="e.g. grant round closing March"
                    value={form.funding_deadline}
                    onChange={(e) => update("funding_deadline", e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-sm text-charcoal">
                  A short description of your project <span className="text-ochre-dark" aria-hidden="true">*</span>
                </Label>
                <Textarea
                  id="message"
                  rows={6}
                  data-testid="contact-message-input"
                  className={`mt-2 ${inputClasses}`}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? "message-error" : undefined}
                />
                {errors.message && (
                  <p id="message-error" role="alert" className="mt-2 text-sm text-destructive" data-testid="contact-message-error">
                    {errors.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="stage" className="text-sm text-charcoal">
                  What stage are you at? <span className="text-charcoal/50">(optional)</span>
                </Label>
                <Select value={form.stage} onValueChange={(v) => update("stage", v)}>
                  <SelectTrigger
                    id="stage"
                    data-testid="contact-stage-select"
                    className={`mt-2 ${inputClasses}`}
                    aria-invalid={!!errors.stage}
                    aria-describedby={errors.stage ? "stage-error" : undefined}
                  >
                    <SelectValue placeholder="Select a stage" />
                  </SelectTrigger>
                  <SelectContent className="bg-cream border-charcoal/20 rounded-sm">
                    {stageOptions.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value}
                        data-testid={`contact-stage-option-${opt.value}`}
                        className="focus:bg-sage"
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.stage && (
                  <p id="stage-error" role="alert" className="mt-2 text-sm text-destructive" data-testid="contact-stage-error">
                    {errors.stage}
                  </p>
                )}
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacy_consent"
                  data-testid="contact-privacy-checkbox"
                  className="mt-0.5 rounded-sm border-charcoal/40 data-[state=checked]:bg-eucalyptus data-[state=checked]:border-eucalyptus"
                  checked={form.privacy_consent}
                  onCheckedChange={(checked) => update("privacy_consent", checked === true)}
                  aria-invalid={!!errors.privacy_consent}
                  aria-describedby={errors.privacy_consent ? "privacy-error" : "privacy-hint"}
                />
                <div>
                  <Label htmlFor="privacy_consent" className="text-sm leading-relaxed text-charcoal/85 font-normal cursor-pointer">
                    I have read the{" "}
                    <Link to="/privacy" className="underline decoration-ochre underline-offset-4 text-ochre-dark hover:text-charcoal transition-colors duration-300">
                      privacy note
                    </Link>{" "}
                    and consent to my details being used to respond to this enquiry.{" "}
                    <span className="text-ochre-dark" aria-hidden="true">*</span>
                  </Label>
                  {errors.privacy_consent && (
                    <p id="privacy-error" role="alert" className="mt-2 text-sm text-destructive" data-testid="contact-privacy-error">
                      {errors.privacy_consent}
                    </p>
                  )}
                </div>
              </div>

              {statusMessage && (
                <div
                  role="status"
                  aria-live="polite"
                  data-testid={`contact-status-${statusMessage.type}`}
                  className={`border p-4 text-sm leading-relaxed rounded-sm ${
                    statusMessage.type === "success"
                      ? "border-eucalyptus/40 bg-eucalyptus/5 text-eucalyptus-deep"
                      : statusMessage.type === "warning"
                        ? "border-ochre/50 bg-ochre/10 text-ochre-dark"
                        : "border-destructive/40 bg-destructive/5 text-destructive"
                  }`}
                >
                  {statusMessage.text}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="bg-eucalyptus text-cream px-8 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
